import { computed, defineAsyncComponent, defineComponent, onMounted, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useMgaReportListStore } from '@/store/mga-reports/mga-report-list.store';
import { useCountryListStore } from '@/store/dictionary/country-list.store';
import { useMgaReportListFilterStore } from '@/store/mga-reports/mga-report-list-filter.store';
import { useCurrencyListStore } from '@/store/dictionary/currency-list.store';
import { useGameTypeListStore } from '@/store/dictionary/game-type-list.store';
import { useGameCategoryListStore } from '@/store/dictionary/game-category-list.store';
import { useTimestampStore } from '@/store/timestamp.store';
import { useTwoFactorStore } from '@/store/two-factor-dialog.store';
import { useMgaReportListCsvStore } from '@/store/mga-reports/mga-report-list-csv.store';
import { usePermissionsStore } from '@/store/permissions.store';
import { MGA_REPORT_PERMISSIONS } from '@/models/app-internal/permissions.model';
import MgaReportList from '@/layouts/mga-page/mga-report-list/mga-report-list.vue';
import MgaTotalList from '@/layouts/mga-page/mga-total-list/mga-total-list.vue';
import MgaReportListFilter from '@/layouts/mga-page/mga-report-list-filter/mga-report-list-filter.vue';
import ReportTimestamp from '@/components/report-timestamp/report-timestamp.vue';
import DownloadActions from '@/components/download-actions/download-actions.vue';
import {
  Download,
  Refresh,
  Filter,
} from '@element-plus/icons-vue';

const icons = {
  Download,
  Refresh,
  Filter,
};

export default defineComponent({
  name: 'MgaPage',
  components: {
    MgaReportList,
    MgaTotalList,
    MgaReportListFilter,
    ReportTimestamp,
    DownloadActions,
    TwoFactorAuthDialog: defineAsyncComponent(
      () => import('@/components/two-factor-auth-dialog/two-factor-auth-dialog.vue'),
    ),
  },
  setup() {
    const mgaReportListStore = useMgaReportListStore();
    const { reportList, isReportListLoading } = storeToRefs(mgaReportListStore);
    const { getReportList } = mgaReportListStore;

    const countryListStore = useCountryListStore();
    const { countryList } = storeToRefs(countryListStore);
    const { getCountryList } = countryListStore;

    const currencyListStore = useCurrencyListStore();
    const { getCurrencyList } = currencyListStore;

    const gameTypeListStore = useGameTypeListStore();
    const { getGameTypeList } = gameTypeListStore;

    const gameCategoryListStore = useGameCategoryListStore();
    const { getGameCategoryList } = gameCategoryListStore;

    const mgaReportListFilterStore = useMgaReportListFilterStore();
    const { isFilterVisible, activeFiltersCount, reportPeriod } = storeToRefs(mgaReportListFilterStore);
    const { setDefaultFilterFormData, setIsFilterVisible } = mgaReportListFilterStore;

    const mgaReportListCsvStore = useMgaReportListCsvStore();
    const { getReportListCsv } = mgaReportListCsvStore;

    const twoFactorStore = useTwoFactorStore();
    const { isTwoFactorDialogVisible } = storeToRefs(twoFactorStore);
    const { setTwoFactorDialogVisibility } = twoFactorStore;

    const { reportPermissions } = storeToRefs(usePermissionsStore());

    const { setTimestampRequired } = useTimestampStore();

    const isTimestampAllowed = computed<boolean>(() => {
      return reportPermissions.value[
        MGA_REPORT_PERMISSIONS.REPORT_PERMISSION_GET_TIMESTAMP
      ];
    });

    const isReportDownloadAllowed = computed<boolean>(() => {
      return reportPermissions.value[
        MGA_REPORT_PERMISSIONS.REPORT_PERMISSION_GET_MGA_REPORT_CSV
      ];
    });

    const openTwoFactorDialog = (isReportTimestampRequired: boolean): void => {
      setTimestampRequired(isReportTimestampRequired);
      setTwoFactorDialogVisibility(true);
    };

    const openFilter = (): void => {
      getCurrencyList();
      getGameTypeList();
      getGameCategoryList();

      setIsFilterVisible(true);
    };

    const refreshFilter = (): void => {
      getReportList();
    };

    onMounted(() => {
      Promise.all([
        getReportList(),
        getCountryList(),
      ]);
    });

    onUnmounted(() => {
      setDefaultFilterFormData();
      setIsFilterVisible(false);
    });

    return {
      icons,
      reportList,
      countryList,
      isFilterVisible,
      openFilter,
      refreshFilter,
      activeFiltersCount,
      reportPeriod,
      isReportListLoading,
      isTimestampAllowed,
      isReportDownloadAllowed,
      isTwoFactorDialogVisible,
      openTwoFactorDialog,
      getReportListCsv,
    };
  },
});
